import {
  type Cell,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Row,
  type SortDirection,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback } from "react";
import type {
  ContextMenuStatus,
  SelectContextMenuHandler,
} from "../../routes/emails/SearchEmailsPage";

type EmailReactTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  onSelectEmail: (id: string) => void;
  onSelectContextMenu: SelectContextMenuHandler;
};

export const EmailReactTable = <T extends Record<string, unknown>>({
  data,
  columns,
  onSelectEmail,
  onSelectContextMenu,
}: EmailReactTableProps<T>) => {
  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const getSortLabel = useCallback((status: false | SortDirection): string => {
    if (status === false) {
      return "";
    }
    if (status === "asc") {
      return " ▲";
    }
    return " ▼";
  }, []);
  return (
    <table className="table table-compact w-full min-w-max">
      <thead className="sticky top-0 bg-primary text-white z-10 font-bold">
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers
              .filter(header => header.id !== "emailId")
              .map(header => (
                <th key={header.id} className="whitespace-nowrap px-3">
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {getSortLabel(header.column.getIsSorted())}
                    </div>
                  )}
                </th>
              ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row: Row<T>) => (
          <tr
            key={row.id}
            className={`cursor-pointer ${
              row.original.read
                ? "bg-gray-100 hover:bg-blue-100/70"
                : "bg-white hover:bg-blue-50"
            }`}
            onClick={() => {
              onSelectEmail(row.getValue("emailId"));
            }}
            onContextMenu={event => {
              event.preventDefault();
              const status: ContextMenuStatus = {
                id: row.getValue("emailId"),
                visible: true,
                clientX: event.clientX,
                clientY: event.clientY,
              };
              onSelectContextMenu(status);
            }}
          >
            {row
              .getVisibleCells()
              .filter((cell: Cell<T, any>) => cell.column.id !== "emailId")
              .map((cell: Cell<T, any>) => (
                <td key={cell.id} className="whitespace-nowrap px-3">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
